import subpixel from '../util/canvasSubpixelOffset';
import LineTool from './LineTool';
import { deepClone } from '../util/deepClone';

/** OHLC Crosshair Tool */
export default class OHLCCrosshairTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'ohlccrosshair',
			name: 'OHLC Crosshair',
			value: null,
			style: this.core.getStyle('tool.ohlcCrosshair')
		};
	}

	initHandles(handles) {
		this.handle = deepClone(handles);
		this.handle[0].getCoords = function () {
			const panel = this.tool.panel;
			const xIdx = panel.core.closestExchangeIndex(
				new Date(this.params.date)
			);
			const xPx = panel.xAxis.primaryScale(xIdx);
			const yPx = panel.size.height / 2;

			return [xPx, yPx];
		};
	}

	renderOverlay(ctx, coords, dataPoint, dataY, dataPointVolume) {
		ctx.beginPath();
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';
		ctx.fillStyle = '#FFF';

		const isIntraday = this.core.state.isIntraday;
		const gap = 10;
		const width = isIntraday ? 134 : 100;
		let height = 100;
		let left = coords[0] + gap;
		let top = dataY - gap - height;
		const volume = dataPointVolume ? dataPointVolume.volume : null;
		const hasVolume = typeof volume === 'number';
		// increase height if volume present
		if (hasVolume) {
			height = 115;
		}
		// flip to left if colliding with right edge
		if (left + width > this.panel.size.width - gap) {
			left = coords[0] - gap - width;
		}

		// flip to bottom if colliding with top edge
		if (top < gap) {
			top = dataY + gap;
		}

		// push up if it now collides with bottom edge
		if (top + height > this.panel.size.height) {
			top = this.panel.size.height - height - gap;
		}

		// white background with dropshadow
		ctx.shadowColor = this.params.style.shadow.color;
		ctx.shadowBlur = this.params.style.shadow.blur;
		ctx.rect(left, top, width, height);
		ctx.fill();
		ctx.shadowColor = null;
		ctx.shadowBlur = null;

		// text

		const padding = 12;
		const rowHeight = 15;
		const rowHeaderPad = 5;
		let rowTop = top + padding;
		let rowLeft = left + padding;
		const dateFormatString = isIntraday ? '%x %X' : '%x';

		// header
		ctx.font = 'bold 12px Arial';
		ctx.fillStyle = '#000000';
		ctx.fillText(
			this.panel.core.timeFormat(dateFormatString)(dataPoint.date),
			rowLeft,
			rowTop
		);

		// first column
		rowTop += rowHeight + rowHeaderPad;
		ctx.font = '11px Arial';
		ctx.fillStyle = '#888888';
		ctx.fillText('Open', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('High', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Low', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Close', rowLeft, rowTop);

		if (hasVolume) {
			rowTop += rowHeight;
			ctx.fillText('Volume', rowLeft, rowTop);
		}

		// second column
		const { yAxis } = this.panel;
		rowTop = top + padding + rowHeight + rowHeaderPad;
		rowLeft = left + width - padding;
		ctx.textAlign = 'right';
		ctx.fillStyle = '#444444';
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.open,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.high,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.low,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.close,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		if (hasVolume) {
			rowTop += rowHeight;
			ctx.fillText(this.panel.yAxis.getFormatValue(volume, '0,.3s', false), rowLeft, rowTop);
		}
	}

	render() {

		const core = this.panel.core;
		const ctx = this.panel.rootToolContext;
		const coords = this.handle[0].getCoords();
		const dataPointIndex = this.getDataPointIndexFromX(coords[0]);
		const dataPoint = core.dataPrimary[dataPointIndex];
		const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(dataPoint.close)));
		const dataVolume = core.data[core.params.symbol].volume;
		let dataPointVolume = null;

		// volume if available
		if (dataVolume) {
			const datasetVolume = core.getFirstDataset(dataVolume);
			dataPointVolume = datasetVolume[dataPointIndex];
		}

		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('TOOL_OHLCCROSSHAIR_RENDER').length) {
			return core.eventEmitter.emit('TOOL_OHLCCROSSHAIR_RENDER', {
				tool: this,
				coords,
				dataPoint,
				dataY,
				dataPointVolume
			});
		}

		// default rendering is below

		// vertical line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.vertical.color;
		ctx.lineWidth = this.params.style.vertical.width;
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], this.panel.size.height);
		ctx.stroke();

		// horizontal line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.horizontal.color;
		ctx.lineWidth = this.params.style.vertical.width;
		ctx.moveTo(0, dataY);
		ctx.lineTo(this.panel.size.width, dataY);
		ctx.stroke();

		// info box
		this.renderOverlay(ctx, coords, dataPoint, dataY, dataPointVolume);

		return this.panel.core.onToolRender(this);
	}

	/** Alternate render method for this tool on lower panels (just render the line) */
	renderLower(panel) {
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();

		// vertical line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.lineWidth = this.params.style.width;
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], panel.size.height);
		ctx.stroke();

		this.handle[0].render();
	}
}
